import React from 'react'
import cx from 'classnames'
import { ControlButton, ControlLink } from '../../Controls'
import withErrorBoundary from '../../ErrorBoundary'
import { registerWorkspace, fetchWorkspaceListAllIDs } from 'webmango-core/effects/workspace'
import { assertSimpleID } from 'webmango-core/selectors/dataset'
import { WaitMessage, ErrorMessage } from '../../StatusMessage'
import { nameToID } from 'webmango-utils'
import '../index.css'

function prefixedNameToID (profile, name) {
  const prefix = profile.email.split('@')[0]
  return `${nameToID(prefix)}-${nameToID(name)}`
}

const EMPTY = []
export default withErrorBoundary(class WorkspaceRegister extends React.PureComponent {
  initialState = {
    defaultName: '',
    id: '',
    name: '',
    error: undefined,
    changedID: false,
    isRegistering: false,
    workspaceIDs: EMPTY
  }

  state = { ...this.initialState }

  componentDidUpdate () {
    this.loadWorkspaceIDs()
  }

  async loadWorkspaceIDs () {
    if (this.state.workspaceIDs !== EMPTY) return
    if (this.isLoadingWorkspaces) return
    if (!this.props.profile || !this.props.authProfile) return
    this.isLoadingWorkspaces = true
    const { workspaceIDs } = await fetchWorkspaceListAllIDs()
    this.isLoadingWorkspaces = false
    this.setState({ workspaceIDs })
  }

  static getDerivedStateFromProps (props, state) {
    if (!props.authProfile || !props.profile || state.defaultName) return null
    const { profile } = props
    if (Object.keys(profile.workspaces).length) return null
    const defaultName = 'Default'
    return {
      name: defaultName,
      id: prefixedNameToID(profile, defaultName),
      defaultName
    }
  }

  onChange = key => event => {
    const { value } = event.target
    this.setState(({ id, changedID }) => {
      if (key === 'name' && !changedID) {
        return {
          [key]: value,
          id: prefixedNameToID(this.props.profile, value)
        }
      }
      if (key === 'id') {
        return {
          id: value.trim(),
          changedID: true
        }
      } else {
        return { [key]: value }
      }
    })
  }

  onBlur = key => () => {
    if (key === 'name') {
      this.setState(({ name, defaultName }) => {
        name = name.trim()
        if (!name) name = defaultName
        return { name }
      })
    } else if (key === 'id') {
      this.setState(({ id, name, changedID }) => {
        id = id.trim()
        if (!id) {
          id = prefixedNameToID(this.props.profile, name)
          changedID = false
        }
        return {
          id,
          changedID
        }
      })
    }
  }

  isIDUnique () {
    if (!this.state.workspaceIDs.length) return true
    if (!this.state.id) return true
    return !(new Set(this.state.workspaceIDs).has(this.state.id))
  }

  getValue (key) {
    const { name, id } = this.state
    if (key === 'name') {
      return name
    }
    if (key === 'id') {
      return id
    }
    return ''
  }

  onSubmit = async () => {
    let ok = false
    const id = this.state.id.trim()
    const name = this.state.name
    this.setState({ error: undefined, isRegistering: true })
    try {
      if (!name) {
        throw new Error('name required')
      }
      if (!id) {
        throw new Error('id required')
      }

      if (!this.isIDUnique()) {
        throw new Error('ID Already Exists')
      }

      assertSimpleID(id)

      this.setState({ error: undefined, isRegistering: true })
      await registerWorkspace({ id, name })
      ok = true
    } catch (error) {
      this.setState({ error, isRegistering: false })
    }

    if (!ok) return
    this.setState({ registering: false })
    this.props.onComplete(this.state)
    this.setState(this.initialState)
  }

  hasWorkspaces () {
    const { profile } = this.props
    return !!profile && Object.keys(profile.workspaces).length
  }

  getError () {
    const id = this.state.id.trim()
    if (!id) { return }
    try {
      assertSimpleID(id)
    } catch (error) {
      return error
    }

    if (!this.isIDUnique()) {
      return new Error('ID Already Exists')
    }
  }

  render () {
    const {
      className,
      authProfile,
      hideCancel,
      profile
    } = this.props
    if (!profile || !authProfile) return null
    const { isRegistering } = this.state

    const hasWorkspaces = this.hasWorkspaces()
    const error = this.getError()

    return (
      <div className={cx('RegisterWorkspace', className)}>
        {!hasWorkspaces && (
          <p className='info'>
            You have no workspaces, you need to create one.
          </p>
        )}
        <div className='LoginRegisterWorkspace Form'>
          <label htmlFor='newWorkspaceName'>
            New Workspace Name
          </label>
          <input
            id='newWorkspaceName'
            type='text'
            value={this.getValue('name')}
            onChange={this.onChange('name')}
            onBlur={this.onBlur('name')}
          />
          <label htmlFor='newWorkspaceID'>
            New Workspace ID
          </label>
          <input
            id='newWorkspaceID'
            type='text'
            className={error && 'error'}
            value={this.getValue('id')}
            onChange={this.onChange('id')}
            onBlur={this.onBlur('id')}
          />
          {(!hasWorkspaces || hideCancel) ? <div /> : (
            <ControlLink
              className='button'
              to={{
                pathname: '/workspaces',
                search: window.location.search
              }}
            >
              Cancel
            </ControlLink>
          )}
          <ControlButton
            style={{marginLeft: 'auto'}}
            className='default'
            onClick={this.onSubmit}
            disabled={!this.getValue('name') || !this.getValue('id') || error}
          >
            Create Workspace
          </ControlButton>
        </div>
        <div>
          <div className={cx('ChangingWorkspace', 'showable', { show: isRegistering })}>
            <WaitMessage title={isRegistering ? `Creating Workspace...` : 'Workspace Created'} />
          </div>
          <div className={cx('ChangingWorkspace', 'showable', { show: error })}>
            {error && <ErrorMessage error={error} />}
          </div>
        </div>
        <div className='LoginDetails'>
          <a onClick={this.onSignOut}>Sign Out</a>&nbsp;&nbsp;
          {authProfile.email}
        </div>
      </div>
    )
  }
})



// WEBPACK FOOTER //
// ./src/components/Login/Workspace/Register.js